import { useQuery, type QueryClient } from "@tanstack/react-query";
import axios from "axios";
import { authGetRequest } from "../api/authClient";
import type { ModulePermissions } from "@variphigen/shared-dashboards";
import { normalizeModulePermissions } from "@variphigen/shared-dashboards";
import { getToken, setToken } from "../services/storageService";
import { hasActiveKeycloakSession, keycloak } from "../auth/keycloak";

export interface ModuleConfigData {
  clientId: string;
  applicationId: string;
  modules: ModulePermissions;
  version: number;
}

export function moduleConfigQueryKey(applicationId: string) {
  return ["module-config", applicationId] as const;
}

async function ensureAccessToken(): Promise<string | null> {
  const current = getToken();
  if (current) return current;
  if (!hasActiveKeycloakSession()) return null;
  try {
    await keycloak.updateToken(30);
    if (keycloak.token) {
      setToken(keycloak.token);
      return keycloak.token;
    }
  } catch {
    // fall through
  }
  return getToken();
}

export async function fetchModuleConfig(
  applicationId: string,
): Promise<ModuleConfigData> {
  if (!applicationId) {
    throw new Error("applicationId is required");
  }

  let bearer = await ensureAccessToken();
  if (!bearer) {
    throw new Error("No access token for module-config");
  }

  const load = (token: string) =>
    authGetRequest(
      `/client/auth/module-config?applicationId=${encodeURIComponent(applicationId)}`,
      undefined,
      { bearer: token },
    );

  const toData = (res: unknown): ModuleConfigData => {
    const data = (res as { data?: ModuleConfigData })?.data ?? (res as ModuleConfigData);
    return {
      clientId: String(data?.clientId ?? ""),
      applicationId: String(data?.applicationId ?? applicationId),
      modules: normalizeModulePermissions(data?.modules ?? data),
      version: Number(data?.version ?? 0),
    };
  };

  try {
    return toData(await load(bearer));
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 401 && hasActiveKeycloakSession()) {
      try {
        await keycloak.updateToken(-1);
        if (keycloak.token) {
          setToken(keycloak.token);
          return toData(await load(keycloak.token));
        }
      } catch {
        // rethrow original below
      }
    }
    throw err;
  }
}

/** Start module-config fetch during auth callback so the sidebar is ready on first paint. */
export function prefetchModuleConfig(
  queryClient: QueryClient,
  applicationId: string,
): void {
  if (!applicationId) return;
  if (!getToken()) return;
  void queryClient.prefetchQuery({
    queryKey: moduleConfigQueryKey(applicationId),
    queryFn: () => fetchModuleConfig(applicationId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useModuleConfig(
  applicationId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: moduleConfigQueryKey(applicationId ?? ""),
    queryFn: () => fetchModuleConfig(applicationId!),
    enabled: Boolean(applicationId) && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return false;
      }
      return failureCount < 1;
    },
  });
}
